import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FixedMenu from '../../../Common/Menus/FixedMenu.jsx';
import {
  isValidClassName,
  cloneEditorValue,
  getClassUsageCounts,
  normalizeClassList,
  renameCustomClass,
  sanitizeClassDefinitions,
  validateEditorDocument,
} from '../../utilities/htmlEditorUtils.js';
import { isKnownBootstrapClass, STYLE_FIELDS } from '../../utilities/styleFields.js';
import GoogleFontPicker from './GoogleFontPicker.jsx';
import { FONT_THEME_CLASS } from '../../utilities/googleFonts.js';

const createEmptyClassDefinition = () => ({
  base: {},
  sm: {},
  md: {},
  lg: {},
  xl: {},
});

const STYLE_GROUPS = [
  {
    title: 'Typography',
    iconClass: 'bi-fonts',
    properties: ['color', 'fontFamily', 'fontSize', 'fontWeight', 'textAlign', 'lineHeight', 'letterSpacing', 'textDecoration'],
  },
  {
    title: 'Spacing and size',
    iconClass: 'bi-bounding-box',
    properties: ['margin', 'marginTop', 'padding', 'width', 'height', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight'],
  },
  {
    title: 'Layout',
    iconClass: 'bi-grid-3x3-gap',
    properties: ['display', 'flexDirection', 'flexGrow', 'flexWrap', 'justifyContent', 'alignItems', 'alignSelf', 'gridTemplateColumns', 'gridTemplateRows', 'gap', 'overflow', 'objectFit'],
  },
  {
    title: 'Position',
    iconClass: 'bi-arrows-move',
    properties: ['position', 'top', 'right', 'bottom', 'left', 'zIndex', 'transform'],
  },
  {
    title: 'Appearance',
    iconClass: 'bi-palette',
    properties: ['backgroundColor', 'border', 'borderRadius', 'boxShadow', 'opacity', 'transition'],
  },
  {
    title: 'Background image',
    iconClass: 'bi-image',
    properties: ['backgroundImage', 'backgroundSize', 'backgroundPosition'],
  },
];

const BREAKPOINTS = [
  ['base', 'All', 'Default styles'],
  ['sm', 'SM', '576px and wider'],
  ['md', 'MD', '768px and wider'],
  ['lg', 'LG', '992px and wider'],
  ['xl', 'XL', '1200px and wider'],
];

const STYLE_OPTIONS = {
  textAlign: ['left', 'center', 'right', 'justify'],
  textDecoration: ['none', 'underline', 'line-through'],
  display: ['block', 'inline', 'inline-block', 'flex', 'inline-flex', 'grid', 'none'],
  flexDirection: ['row', 'row-reverse', 'column', 'column-reverse'],
  flexWrap: ['nowrap', 'wrap', 'wrap-reverse'],
  justifyContent: ['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly'],
  alignItems: ['stretch', 'flex-start', 'center', 'flex-end', 'baseline'],
  alignSelf: ['auto', 'stretch', 'flex-start', 'center', 'flex-end', 'baseline'],
  position: ['static', 'relative', 'absolute', 'fixed', 'sticky'],
  overflow: ['visible', 'hidden', 'auto', 'scroll', 'clip'],
  objectFit: ['contain', 'cover', 'fill', 'none', 'scale-down'],
  backgroundSize: ['auto', 'cover', 'contain'],
  backgroundPosition: ['center', 'top', 'right', 'bottom', 'left'],
};

const STYLE_PLACEHOLDERS = {
  color: '#212529',
  backgroundColor: '#ffffff',
  fontSize: '1rem',
  fontWeight: '400 or 700',
  lineHeight: '1.5',
  letterSpacing: '0.05rem',
  margin: '1rem auto',
  marginTop: '1rem',
  padding: '1rem',
  width: '100%',
  height: 'auto',
  gap: '1rem',
  border: '1px solid #dee2e6',
  borderRadius: '0.5rem',
  boxShadow: '0 0.25rem 1rem rgba(0,0,0,.1)',
  opacity: '1',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: 'auto',
  transform: 'translateY(0)',
  transition: 'all 200ms ease',
};

const friendlyPropertyName = (property) => String(property)
  .replace(/([A-Z])/g, ' $1')
  .replace(/^./, (character) => character.toUpperCase());

const StyleField = ({ property, value, onChange, onPreviewFont }) => {
  const label = friendlyPropertyName(property);
  const options = STYLE_OPTIONS[property];
  const isColor = property === 'color' || property === 'backgroundColor';
  const colorValue = /^#[0-9a-f]{6}$/i.test(value) ? value : '#000000';

  if (property === 'fontFamily') {
    return (
      <GoogleFontPicker
        id="class-style-fontFamily"
        label="Font family"
        value={value}
        onChange={onChange}
        onPreview={onPreviewFont}
        compact
      />
    );
  }

  return (
    <div className="class-style-field">
      <label className="form-label small mb-1" htmlFor={`class-style-${property}`}>{label}</label>
      <div className="input-group input-group-sm">
        {isColor ? (
          <input
            className="form-control form-control-color class-style-color-input"
            type="color"
            value={colorValue}
            onChange={(event) => onChange(event.target.value)}
            title={`Choose ${label.toLowerCase()}`}
            aria-label={`Choose ${label.toLowerCase()}`}
          />
        ) : null}
        {options ? (
          <select
            id={`class-style-${property}`}
            className="form-select"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            aria-label={`${label} value`}
          >
            <option value="">Not set</option>
            {options.map((option) => <option value={option} key={option}>{option}</option>)}
          </select>
        ) : (
          <input
            id={`class-style-${property}`}
            className="form-control"
            value={value}
            placeholder={STYLE_PLACEHOLDERS[property] || 'Not set'}
            onChange={(event) => onChange(event.target.value)}
            aria-label={`${label} value`}
          />
        )}
        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={() => onChange('')}
          disabled={!value}
          title={`Clear ${label.toLowerCase()}`}
          aria-label={`Clear ${label.toLowerCase()}`}
        >
          <i className="bi bi-x-lg" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  );
};

StyleField.propTypes = {
  property: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onPreviewFont: PropTypes.func,
};

const ClassStyleMenu = ({
  expanded,
  setExpanded,
  editorDocument,
  setEditorDocument,
  preferredClassName = '',
  requestConfirmation,
  onPreviewFonts,
}) => {
  const [newClassName, setNewClassName] = useState('');
  const [selectedClassName, setSelectedClassName] = useState('');
  const [classQuery, setClassQuery] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [activeBreakpoint, setActiveBreakpoint] = useState('base');
  const [customStyleProperty, setCustomStyleProperty] = useState('');
  const [customStyleValue, setCustomStyleValue] = useState('');
  const [openGroups, setOpenGroups] = useState(['Typography']);
  const [error, setError] = useState('');
  const previousOpenRequest = useRef({ expanded: false, preferredClassName: '' });

  const safeDocument = useMemo(
    () => validateEditorDocument(editorDocument).document,
    [editorDocument]
  );
  const customClasses = useMemo(
    () => sanitizeClassDefinitions(safeDocument.classes),
    [safeDocument.classes]
  );
  const classNameKey = Object.keys(customClasses).sort().join('\n');
  const customClassNames = useMemo(
    () => classNameKey ? classNameKey.split('\n').filter((name) => name !== FONT_THEME_CLASS) : [],
    [classNameKey]
  );
  const usageCounts = useMemo(
    () => getClassUsageCounts(safeDocument.root, customClassNames),
    [customClassNames, safeDocument.root]
  );
  const visibleClassNames = customClassNames.filter((className) => (
    className.toLowerCase().includes(classQuery.trim().toLowerCase())
  ));
  const activeClassName = selectedClassName && customClassNames.includes(selectedClassName)
    ? selectedClassName
    : customClassNames[0] || '';
  const requestedClassName = customClassNames.includes(preferredClassName)
    ? preferredClassName
    : customClassNames[0] || '';

  useEffect(() => {
    const previous = previousOpenRequest.current;
    const isOpening = expanded && !previous.expanded;
    const preferredClassChanged = expanded && previous.preferredClassName !== preferredClassName;

    if (isOpening || preferredClassChanged) {
      setSelectedClassName(requestedClassName);
    }

    previousOpenRequest.current = { expanded, preferredClassName };
  }, [expanded, requestedClassName, preferredClassName]);

  useEffect(() => {
    if (!expanded) setClassQuery('');
  }, [expanded]);

  const updateDocument = (updater) => {
    setEditorDocument((previous) => validateEditorDocument(updater(validateEditorDocument(previous).document)).document);
  };

  const createCustomClass = () => {
    const safeClass = String(newClassName || '').trim();
    if (!isValidClassName(safeClass) || safeClass === FONT_THEME_CLASS) {
      setError('Enter a valid custom class name.');
      return;
    }

    if (isKnownBootstrapClass(safeClass)) {
      setError('That name belongs to a Bootstrap class. Select it instead of creating a custom class.');
      return;
    }

    setError('');
    updateDocument((current) => ({
      ...current,
      classes: {
        ...(current.classes || {}),
        [safeClass]: current.classes?.[safeClass] || createEmptyClassDefinition(),
      },
    }));
    setSelectedClassName(safeClass);
    setNewClassName('');
  };

  const updateStyle = (className, breakpoint, property, value) => {
    if (!className || !customClasses[className]) {
      return;
    }

    updateDocument((current) => {
      const breakpointStyles = { ...(current.classes?.[className]?.[breakpoint] || {}) };
      if (value === '') {
        delete breakpointStyles[property];
      } else {
        breakpointStyles[property] = value;
      }

      return {
        ...current,
        classes: {
          ...(current.classes || {}),
          [className]: {
            ...(current.classes?.[className] || createEmptyClassDefinition()),
            [breakpoint]: breakpointStyles,
          },
        },
      };
    });
  };

  const renameClass = () => {
    const safeRename = renameValue.trim();
    if (!activeClassName || !isValidClassName(safeRename) || safeRename === FONT_THEME_CLASS || isKnownBootstrapClass(safeRename)) {
      setError('Enter a valid replacement class name.');
      return;
    }

    setError('');
    setEditorDocument((previous) => renameCustomClass(previous, activeClassName, safeRename));
    setSelectedClassName(safeRename);
    setRenameValue('');
  };

  const deleteClass = () => {
    if (!activeClassName) return;
    const onConfirm = () => {
      updateDocument((current) => {
      const next = cloneEditorValue(current);
      delete next.classes[activeClassName];

      const removeClassFromNode = (node) => {
        if (!node || node.type === 'text') {
          return node;
        }

        return {
          ...node,
          attributes: {
            ...(node.attributes || {}),
            class: normalizeClassList(node.attributes?.class).filter((entry) => entry !== activeClassName),
          },
          children: (node.children || []).map(removeClassFromNode),
        };
      };

      next.root = removeClassFromNode(next.root);
      return next;
      });
      setSelectedClassName('');
    };

    if (typeof requestConfirmation === 'function') {
      requestConfirmation({
        title: `Delete .${activeClassName}?`,
        message: 'This reusable style will be removed from every item that uses it.',
        confirmLabel: 'Delete class',
        variant: 'danger',
        onConfirm,
      });
    } else {
      onConfirm();
    }
  };

  const addCustomStyleProperty = () => {
    const property = customStyleProperty.trim();
    if (!activeClassName || !property) {
      setError('Enter a style property name.');
      return;
    }

    setError('');
    updateStyle(activeClassName, activeBreakpoint, property, customStyleValue);
    setCustomStyleProperty('');
    setCustomStyleValue('');
  };

  return (
    <FixedMenu
      className="new-blog-fixed-menu"
      animate
      title="Class Editor"
      iconClass="bi-brush"
      expanded={expanded}
      setExpanded={setExpanded}
    >
      {error ? <div className="alert alert-danger py-2 small">{error}</div> : null}

      <div className="class-style-workspace">
        <aside className="class-style-sidebar">
          <section className="class-style-section">
            <h6>Create reusable class</h6>
            <div className="input-group input-group-sm mb-3">
              <input
                className="form-control"
                value={newClassName}
                placeholder="portfolio-section"
                onChange={(event) => setNewClassName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    createCustomClass();
                  }
                }}
              />
              <button className="btn btn-outline-success" type="button" onClick={createCustomClass}>
                <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>Create
              </button>
            </div>

            <div className="class-style-section-heading">
              <h6>Custom classes</h6>
              <span className="badge text-bg-light">{customClassNames.length}</span>
            </div>
            {customClassNames.length ? (
              <>
                <label className="form-label small fw-semibold" htmlFor="class-style-search">Find a class</label>
                <div className="input-group input-group-sm mb-3">
                  <span className="input-group-text"><i className="bi bi-search" aria-hidden="true"></i></span>
                  <input
                    id="class-style-search"
                    className="form-control"
                    type="search"
                    value={classQuery}
                    placeholder="Search custom classes"
                    onChange={(event) => setClassQuery(event.target.value)}
                  />
                </div>
              </>
            ) : null}
            {visibleClassNames.length ? (
              <div className="class-style-class-list" role="listbox" aria-label="Custom classes">
                {visibleClassNames.map((className) => (
                  <button
                    className={`btn btn-sm text-start ${className === activeClassName ? 'btn-primary' : 'btn-outline-secondary'}`}
                    type="button"
                    role="option"
                    aria-selected={className === activeClassName}
                    onClick={() => setSelectedClassName(className)}
                    key={className}
                  >
                    <i className="bi bi-brush me-2" aria-hidden="true"></i>
                    <span className="class-style-list-name">.{className}</span>
                    <span className="badge text-bg-light class-style-usage-count">
                      {usageCounts[className]} {usageCounts[className] === 1 ? 'use' : 'uses'}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-muted small mb-0">
                {customClassNames.length ? 'No matching classes.' : 'Create a class to begin styling.'}
              </p>
            )}
          </section>

          {activeClassName ? (
            <section className="class-style-section class-style-manage">
              <div className="class-style-heading">
                <span className="class-style-name">.{activeClassName}</span>
                <small className="text-muted">Updates every element using this class.</small>
              </div>
              <div className="input-group input-group-sm mt-2">
                <input
                  className="form-control"
                  value={renameValue}
                  placeholder="Rename class"
                  onChange={(event) => setRenameValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      renameClass();
                    }
                  }}
                />
                <button className="btn btn-outline-primary" type="button" onClick={renameClass} title="Rename class">
                  <i className="bi bi-check-lg" aria-hidden="true"></i>
                </button>
                <button className="btn btn-outline-danger" type="button" onClick={deleteClass} title="Delete class">
                  <i className="bi bi-trash" aria-hidden="true"></i>
                </button>
              </div>
            </section>
          ) : null}
        </aside>

        <main className="class-style-editor">
          {activeClassName ? (
            <>
              <div className="class-style-editor-header">
                <div>
                  <small className="text-muted">Editing styles for</small>
                  <h5 className="class-style-name mb-0">.{activeClassName}</h5>
                </div>
                <span className="badge text-bg-primary">{activeBreakpoint === 'base' ? 'All screens' : `${activeBreakpoint.toUpperCase()} and wider`}</span>
              </div>

              <div className="class-breakpoint-tabs" role="tablist" aria-label="Responsive breakpoint">
                {BREAKPOINTS.map(([breakpoint, label, title]) => {
                  const propertyCount = Object.keys(customClasses[activeClassName]?.[breakpoint] || {}).length;
                  return (
                    <button
                      className={`btn btn-sm ${breakpoint === activeBreakpoint ? 'btn-primary' : 'btn-outline-primary'}`}
                      type="button"
                      onClick={() => setActiveBreakpoint(breakpoint)}
                      aria-selected={breakpoint === activeBreakpoint}
                      role="tab"
                      title={title}
                      key={breakpoint}
                    >
                      <span>{label}</span>
                      {propertyCount ? <span className="class-breakpoint-count">{propertyCount}</span> : null}
                    </button>
                  );
                })}
              </div>

              <div className="class-style-groups">
                {STYLE_GROUPS.map((group) => (
                  <details className="class-style-group" open={openGroups.includes(group.title)} key={group.title}>
                    <summary onClick={(event) => {
                      event.preventDefault();
                      setOpenGroups((current) => current.includes(group.title)
                        ? current.filter((title) => title !== group.title)
                        : [...current, group.title]);
                    }}>
                      <i className={`bi ${group.iconClass} me-2`} aria-hidden="true"></i>{group.title}
                    </summary>
                    <div className="class-style-group-fields">
                      {group.properties.map((property) => (
                        <StyleField
                          property={property}
                          value={customClasses[activeClassName]?.[activeBreakpoint]?.[property] || ''}
                          onChange={(value) => updateStyle(activeClassName, activeBreakpoint, property, value)}
                          onPreviewFont={(family) => onPreviewFonts?.([family])}
                          key={`${activeBreakpoint}-${property}`}
                        />
                      ))}
                    </div>
                  </details>
                ))}
              </div>

              <details className="class-style-group class-style-advanced">
                <summary><i className="bi bi-code-slash me-2" aria-hidden="true"></i>Advanced styling</summary>
                <div className="class-style-advanced-content">
                  <div className="input-group input-group-sm mb-3">
                    <input
                      className="form-control"
                      value={customStyleProperty}
                      placeholder="Style name"
                      onChange={(event) => setCustomStyleProperty(event.target.value)}
                    />
                    <input
                      className="form-control"
                      value={customStyleValue}
                      placeholder="Style value"
                      onChange={(event) => setCustomStyleValue(event.target.value)}
                    />
                    <button className="btn btn-outline-primary" type="button" onClick={addCustomStyleProperty}>Add</button>
                  </div>

                  {Object.keys(customClasses[activeClassName]?.[activeBreakpoint] || {})
                    .filter((property) => !STYLE_FIELDS.includes(property))
                    .map((property) => (
                      <StyleField
                        property={property}
                        value={customClasses[activeClassName]?.[activeBreakpoint]?.[property] || ''}
                        onChange={(value) => updateStyle(activeClassName, activeBreakpoint, property, value)}
                        key={`custom-${activeBreakpoint}-${property}`}
                      />
                    ))}
                </div>
              </details>
            </>
          ) : (
            <div className="class-style-empty">
              <i className="bi bi-palette" aria-hidden="true"></i>
              <h5>Select or create a custom class</h5>
              <p className="text-muted mb-0">Custom styles are reusable and update every element that uses the class.</p>
            </div>
          )}
        </main>
      </div>
    </FixedMenu>
  );
};

ClassStyleMenu.propTypes = {
  expanded: PropTypes.bool.isRequired,
  setExpanded: PropTypes.func.isRequired,
  editorDocument: PropTypes.object,
  setEditorDocument: PropTypes.func.isRequired,
  preferredClassName: PropTypes.string,
  requestConfirmation: PropTypes.func,
  onPreviewFonts: PropTypes.func,
};

export default ClassStyleMenu;
